import React from 'react';
import { 
  FaRocket, 
  FaLightbulb, 
  FaHandshake, 
  FaBullseye, 
  FaCode, 
  FaChartLine, 
  FaUsers, 
  FaClock, 
  FaStar, 
  FaHeart 
} from 'react-icons/fa';
import './About.css';
import usePageMeta from '../../hooks/usePageMeta';

interface Stat {
  icon: React.ReactNode;
  number: string;
  label: string;
}

interface Value {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface Differential {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const About: React.FC = () => {
  usePageMeta({
    title: 'Sobre Nós',
    description:
      'Conheça a UpdateCommit: quem somos, nossos valores e por que empresas confiam em nós para integrar sistemas, automatizar processos e desenvolver software sob medida.',
    path: '/about',
  });

  const stats: Stat[] = [
    { icon: <FaRocket />, number: '100+', label: 'Evoluções Versionadas' },
    { icon: <FaCode />, number: '70 mil+', label: 'Linhas Adicionadas' },
    { icon: <FaStar />, number: '5 anos', label: 'De Evolução Contínua' }
  ];

  const values: Value[] = [
    {
      icon: <FaLightbulb />,
      title: 'Inovação Constante',
      description: 'Não seguimos tendências, criamos elas. Estamos sempre um passo à frente, explorando novas tecnologias e metodologias para entregar soluções que surpreendem.'
    },
    {
      icon: <FaHeart />,
      title: 'Paixão pelo que Fazemos',
      description: 'Cada linha de código é escrita com dedicação. Cada projeto é tratado como se fosse nosso. Transformamos desafios em oportunidades de crescimento.'
    },
    {
      icon: <FaHandshake />,
      title: 'Parceria Verdadeira',
      description: 'Você não é apenas um cliente, é um parceiro. Caminhamos juntos do início ao fim, garantindo que sua visão se torne realidade com todo suporte necessário.'
    },
    {
      icon: <FaChartLine />,
      title: 'Resultados Mensuráveis',
      description: 'Bonito não basta, tem que funcionar! Focamos em soluções que geram ROI real, aumentam produtividade e fazem seu negócio crescer de verdade.'
    }
  ];

  const differentials: Differential[] = [
    {
      icon: <FaCode />,
      title: 'Código Limpo & Escalável',
      description: 'Desenvolvemos pensando no futuro. Código documentado, testado e preparado para crescer junto com seu negócio.'
    },
    {
      icon: <FaClock />,
      title: 'Agilidade na Entrega',
      description: 'Trabalhamos com metodologias ágeis para entregas rápidas e incrementais. Você vê resultados desde o primeiro sprint.'
    },
    {
      icon: <FaUsers />,
      title: 'Time Multidisciplinar',
      description: 'Desenvolvedores, designers e especialistas em negócios trabalhando em sintonia perfeita para criar a solução ideal.'
    }
  ];

  return (
    <div className="about-container">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-content">
          <span className="about-badge">Sobre Nós</span>
          <h1 className="about-hero-title">
            Transformamos Ideias em <span className="gradient-text">Realidade Digital</span>
          </h1>
          <p className="about-hero-description">
            Na <strong className="highlight-purple">UpdateCommit</strong>, combinamos tecnologia de ponta 
            com estratégia para entregar soluções que realmente fazem a diferença no seu negócio.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="about-stats-section reveal">
        <div className="about-container-inner">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-icon">{stat.icon}</div>
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
          <p className="stats-source">
            Indicadores consolidados a partir do Git e da trajetória técnica da UpdateCommit.
          </p>
        </div>
      </section>

      {/* Nossa História */}
      <section className="about-story-section reveal">
        <div className="about-container-inner">
          <div className="story-header">
            <FaBullseye className="story-icon" />
            <h2 className="section-title">Nossa História</h2>
          </div>
          <div className="story-content">
            <p className="story-text">
              Nascemos da frustração de ver pequenas empresas com grandes ideias, mas sem acesso a 
              soluções tecnológicas de qualidade que cabiam no bolso. Em um mercado dominado por 
              grandes players com preços inacessíveis e agências que ofereciam soluções genéricas, 
              vimos uma oportunidade.
            </p>
            <p className="story-text">
              Criamos a <strong className="highlight-purple">UpdateCommit</strong> com uma missão clara: 
              democratizar o acesso à tecnologia de ponta. Acreditamos que todo negócio, independente 
              do tamanho, merece ter as ferramentas necessárias para competir no mercado digital. 
              E é isso que fazemos todos os dias: 
              <strong className="highlight-purple"> transformamos complexidade em simplicidade, código 
              em resultados, e visão em realidade</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="about-values-section reveal">
        <div className="about-container-inner">
          <div className="section-header">
            <h2 className="section-title-center">O que Nos Move</h2>
            <p className="section-subtitle">Nossos valores guiam cada decisão e cada linha de código</p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-icon-wrapper">
                  <div className="value-icon">{value.icon}</div>
                </div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="about-differentials-section reveal">
        <div className="about-container-inner">
          <div className="section-header">
            <h2 className="section-title-center">Por Que Escolher a UpdateCommit?</h2>
            <p className="section-subtitle">Tecnologia de ponta com atendimento humanizado</p>
          </div>
          <div className="differentials-grid">
            {differentials.map((diff, index) => (
              <div key={index} className="differential-card">
                <div className="differential-icon-wrapper">
                  <div className="differential-icon">{diff.icon}</div>
                </div>
                <h3 className="differential-title">{diff.title}</h3>
                <p className="differential-description">{diff.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="about-cta-section reveal reveal--scale">
        <div className="cta-card">
          <h2 className="cta-title">Pronto para Decolar? 🚀</h2>
          <p className="cta-description">
            Vamos transformar seus desafios em oportunidades. 
            Conte-nos sobre seu projeto e descubra como podemos ajudar seu negócio a alcançar novos patamares.
          </p>
          <a
            href="https://wa.me/5521936185311?text=Olá!%20Vim%20pela%20página%20Empresa%20da%20UpdateCommit%20e%20quero%20conversar%20sobre%20um%20projeto."
            target="_blank"
            rel="noopener noreferrer"
            className="cta-button"
          >
            <span>Vamos Conversar!</span>
            <FaRocket className="button-icon" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;
